/**
 * Surface entity and its supporting shapes.
 *
 * A Surface is an independent UI region on a page — composed of ComponentNodes
 * in an adjacency-list model. findRootComponent is colocated here because it
 * operates on ComponentNode arrays directly (folded in from storage-utils as
 * part of Plan 5 / audit finding #4 closure).
 */

import type { PageId, SurfaceId, WorkspaceId } from '@deckspace/ids';
import type { SurfaceTheme } from './tokens.js';

// --- Composition types (adjacency list model) ---

export interface ComponentNode {
  /** Unique within this composition. Root node must have id "root". */
  id: string;
  /** Catalog component type (e.g., "Row", "Chart", "Text"). */
  type: string;
  /** IDs of child nodes. Only layout components should have children. */
  children?: string[];
  /** Component-specific props, validated against catalog Zod schema at runtime. */
  props?: Record<string, unknown>;
}

/**
 * A surface — independent UI region on a page.
 * Aligned with A2UI Surface concept.
 */
export interface Surface {
  id: SurfaceId;
  pageId: PageId;
  workspaceId: WorkspaceId;
  catalogId: string;
  components: ComponentNode[];
  theme?: SurfaceTheme;
  createdAt: string;
  updatedAt: string;
}

/** Returned by schema operations and dataset deletion to inform Claude of cascading effects. */
export interface AffectedSurface {
  pageId: PageId;
  pageName: string;
  surfaceId: SurfaceId;
  nodeId: string;
  nodeType: string;
  impact: string;
}

/** Input for creating a surface. pageId is nullable — null means "resolve to first page". */
export interface SurfaceCreateInput {
  workspaceId: WorkspaceId;
  pageId: PageId | null;
  catalogId?: string;
  components: ComponentNode[];
  theme?: SurfaceTheme;
}

/** Typed update for a surface — accepts partial component list and/or theme. */
export interface SurfaceUpdate {
  components?: ComponentNode[];
  theme?: SurfaceTheme;
}

/**
 * Find the root component node in a composition tree.
 * Prefers a node with id "root", falls back to the first component.
 */
export function findRootComponent(components: ComponentNode[]): ComponentNode | undefined {
  return components.find((c) => c.id === 'root') ?? components[0];
}
